/**
 * @file SaveIndicator.tsx
 * @description Признак сохранения черновика (phase19.16 Р1; uiux-design-r1
 * §0.1, §1 «Черновик не сохранился»): «Сохранено в черновик · 14:32»,
 * «Сохраняем…», «Не сохранилось — [Повторить]»; при конфликте версий —
 * полоса «Черновик изменил <кто> — [Обновить]».
 */

import { RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { NoticeStrip } from "../ui/NoticeStrip";
import type { AutosaveApi } from "../../hooks/site-view/useAutosavePart";
import { formatEditMoment } from "../../lib/site-view/changes";

/** Нужна только часть API автосохранения — значение не важно. */
type SaveState = Pick<AutosaveApi<unknown>, "status" | "savedAt" | "error" | "conflict" | "retry" | "reload">;

/** Текст по статусу — реестром. */
const STATUS_TEXT: Readonly<Record<SaveState["status"], string | null>> = {
  idle: null,
  pending: "Есть несохранённые правки…",
  saving: "Сохраняем…",
  saved: null,
  error: null,
  conflict: null,
};

function timeLabel(date: Date): string {
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

export function SaveIndicator({ state }: { state: SaveState }) {
  const [reloading, setReloading] = useState(false);

  if (state.status === "conflict") {
    const who = state.conflict?.editedBy?.name ?? "другой сотрудник";
    const when = state.conflict?.editedAt ? ` в ${formatEditMoment(state.conflict.editedAt)}` : "";
    return (
      <NoticeStrip
        tone="error"
        action={
          <Button
            variant="outline"
            loading={reloading}
            onClick={() => {
              setReloading(true);
              void state.reload().finally(() => setReloading(false));
            }}
          >
            <RefreshCw aria-hidden />
            Обновить
          </Button>
        }
      >
        Черновик изменил {who}{when} — ваши последние правки не сохранены. Обновите, чтобы продолжить с его версией.
      </NoticeStrip>
    );
  }

  if (state.status === "error") {
    return (
      <NoticeStrip
        tone="warning"
        action={
          <Button variant="outline" onClick={state.retry}>
            Повторить
          </Button>
        }
      >
        {state.error ?? "Изменения не сохранились — проверьте связь."} Пока они есть только в этом окне.
      </NoticeStrip>
    );
  }

  const text = STATUS_TEXT[state.status] ?? (state.savedAt ? `Сохранено в черновик · ${timeLabel(state.savedAt)}` : null);
  if (!text) return null;
  return (
    <p className="type-caption text-on-surface-variant" role="status" aria-live="polite">
      {text}
    </p>
  );
}
